/* eslint-disable camelcase */

import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate } from 'react-router-dom'
import { getUserTypeFromToken } from '../../_helpers/cookie'
import { useAppDispatch, useAppSelector } from '../../_helpers/hooks'
import PricingAlertModal from '../../components/pricingAlert'
import PlanLimitExceededAlert from '../../components/pricingAlert/planLimitExceeded'
import NewBookingDrawer from '../../features/newBooking'
import { GetSuperAdminProfileDrawerChildren } from '../../features/profileDrawer/getSuperAdminProfileDrawerChildren'
import useDebounce from '../../hooks/useDebounce'
import { closeDrawer, newBookingData } from '../../slices/newBookingSlice'
import {
  pricingData,
  setPlanLimitExceededAlert,
  setPricingAlertModal,
} from '../../slices/pricingSlice'
import {
  changeProfileFilters,
  resetProfileBusinessListData,
  superAdminBusinessState,
  superAdminSwitchProfile,
  switchSuperAdminBusinessList,
} from '../../slices/superAdmin/businessSlice'
import { getLanguages } from '../../slices/translationSlice'
import { userState } from '../../slices/userSlice'
import AdminDrawer from './Drawer'
import { default as AdminHeader } from './header'
import { switchBusinessList } from '../../slices/businessSlice'


export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }
  const handleDrawerOnPopoverClick = () => {
    setMobileOpen(false)
  }
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const userInformation = useAppSelector(userState)
  // eslint-disable-next-line camelcase
  const { userData, active_business_id } = userInformation
  const newBookingState = useAppSelector(newBookingData)
  const { drawerMode } = newBookingState
  const pricingState = useAppSelector(pricingData)
  const {
    showPricingAlert,
    pricingAlertContent,
    showPlanLimitExceededAlert,
    planLimitExceededContent,
  } = pricingState
  const isSuperAdmin = getUserTypeFromToken() === 'super_admin'
  const superAdminBusinessDetails = useAppSelector(superAdminBusinessState)
  const {
    completeProfileBusinessList,
    profileBusinessList,
    profileFilters,
    isLoadingProfileBusinessList,
  } = superAdminBusinessDetails
  const [searchValue, setSearchValue] = useState<string | number | undefined>('')
  const debouncedValue = useDebounce<string | number | undefined>(searchValue, 500)

  React.useEffect(() => {
    dispatch(getLanguages())

    if (isSuperAdmin) {
      if (profileFilters.page === 1) {
        dispatch(resetProfileBusinessListData())
      }
      dispatch(switchSuperAdminBusinessList(profileFilters))
    }
    else {
      dispatch(switchBusinessList())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isSuperAdmin, profileFilters])

  React.useEffect(() => {
    if (drawerMode) {
      document.body.classList.remove('add-scroll')
      document.body.classList.add('no-scroll')
    } else {
      document.body.classList.remove('no-scroll')
      document.body.classList.add('add-scroll')
    }
  }, [drawerMode])

  const closeNewBookingDrawer = () => {
    dispatch(closeDrawer())
  }

  const handleSwitchProfile = (profileType: string, profileData?: { id: number }) => {
    setSearchValue('')
    let payload
    if (profileType === 'business' && profileData) {
      payload = {
        type: profileType,
        // eslint-disable-next-line camelcase
        business_id: profileData.id,
      }
    } else {
      payload = {
        type: profileType,
      }
    }
    dispatch(superAdminSwitchProfile(payload))
  }

  const handleConfigurationClick = (onClose: () => void) => {
    navigate('/super-admin/configuration')
    onClose()
  }

  const handleScroll = () => {
    dispatch(
      changeProfileFilters({
        ...profileFilters,
        page: profileFilters.page + 1,
      }),
    )
  }

  const handleSearch = (searchValue: string | number | undefined) => {
    dispatch(resetProfileBusinessListData())
    dispatch(
      changeProfileFilters({
        page: 1,
        search: searchValue,
      }),
    )
  }

  useEffect(() => {
    if (isSuperAdmin && debouncedValue) {
      handleSearch(debouncedValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, isSuperAdmin])

  const profileDrawerChildren = isSuperAdmin
    ? GetSuperAdminProfileDrawerChildren({
        completeProfileBusinessList,
        profileBusinessList,
        profileFilters,
        handleSwitchProfile,
        handleConfigurationClick,
        handleScroll,
        isLoadingProfileBusinessList,
        userData,
        active_business_id,
        theme,
        t,
        searchValue,
        setSearchValue,
        debouncedValue,
        handleSearch,
      })
    : {}
  return (
    <>
      <AdminHeader
        handleDrawerOnPopoverClick={handleDrawerOnPopoverClick}
        handleDrawerToggle={handleDrawerToggle}
        userData={userData}
        profileDrawerChildren={profileDrawerChildren}
      >
        <Box component='nav'>
          <AdminDrawer
            mobileOpen={mobileOpen}
            handleDrawerToggle={handleDrawerToggle}
            userData={userData}
            handleDrawerOnPopoverClick={handleDrawerOnPopoverClick}
            profileDrawerChildren={profileDrawerChildren}
          />
        </Box>
      </AdminHeader>

      <Box sx={{ overflowX: 'hidden', overflowY: 'hidden' }}>
        <NewBookingDrawer drawerStatus={drawerMode} updateDrawer={closeNewBookingDrawer} />
        <Outlet />
      </Box>
      <PricingAlertModal
        showAlert={showPricingAlert}
        content={pricingAlertContent}
        onAlertClose={() => dispatch(setPricingAlertModal({ status: false }))}
      />
      <PlanLimitExceededAlert
        showAlert={showPlanLimitExceededAlert}
        content={planLimitExceededContent}
        onAlertClose={() => dispatch(setPlanLimitExceededAlert({ status: false }))}
      />
    </>
  )
}
